import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { servicePredictorApiService } from '../../services/servicePredictorApiService';
import type {
  FindBNsRequest,
  FindBNsResponse,
  FindOperatorsRequest,
  FindOperatorsResponse,
  CalculateLostRequest,
  CalculateLostResponse,
  ServicePredictorState,
} from '../types';
import type { SiteInfo } from '../../hooks/useNearbyBaseNodes';
import { CALCULATE_LOST_RESPONSE } from '../../mockData';

export const findBNs = createAsyncThunk<
  FindBNsResponse,
  FindBNsRequest,
  { rejectValue: { error?: string; canceled?: boolean } }
>(
  'servicePredictor/findBNs',
  async ({ params, signal }, { rejectWithValue }) => {
    try {
      const result = await servicePredictorApiService.findBNs({
        ...params,
        ...(signal && { signal }),
      });

      if (result.canceled) {
        return rejectWithValue({ canceled: true });
      }

      if (result.error) {
        return rejectWithValue({ error: result.error });
      }

      return result;
    } catch (error: any) {
      return rejectWithValue({
        error: error.message || 'Failed to find base nodes',
      });
    }
  }
);

export const findOperators = createAsyncThunk<
  FindOperatorsResponse,
  FindOperatorsRequest,
  { rejectValue: { error?: string; canceled?: boolean } }
>(
  'servicePredictor/findOperators',
  async ({ params, signal }, { rejectWithValue }) => {
    try {
      const result = await servicePredictorApiService.findOperators({
        ...params,
        ...(signal && { signal }),
      });

      if (result.canceled) {
        return rejectWithValue({ canceled: true });
      }

      if (result.error) {
        return rejectWithValue({ error: result.error });
      }

      return result;
    } catch (error: any) {
      return rejectWithValue({
        error: error.message || 'Failed to find operators',
      });
    }
  }
);

export const loadCountryList = createAsyncThunk<
  { countryList?: any[]; error?: string; canceled?: boolean },
  { signal?: AbortSignal },
  { rejectValue: { error?: string; canceled?: boolean } }
>(
  'servicePredictor/loadCountryList',
  async ({ signal }, { rejectWithValue }) => {
    try {
      const result = await servicePredictorApiService.loadCountryList({
        ...(signal && { signal }),
      });

      if (result.canceled) {
        return rejectWithValue({ canceled: true });
      }

      if (result.error) {
        return rejectWithValue({ error: result.error });
      }

      return result;
    } catch (error: any) {
      return rejectWithValue({
        error: error.message || 'Failed to load country list',
      });
    }
  }
);

export const calculateLost = createAsyncThunk<
  CalculateLostResponse,
  CalculateLostRequest,
  { rejectValue: { error?: string; canceled?: boolean } }
>(
  'servicePredictor/calculateLost',
  async ({ params, signal }, { rejectWithValue }) => {
    // For now, return mock response for every call
    // return {
    //   sectorResults: CALCULATE_LOST_RESPONSE,
    //   isMultiSector: !!CALCULATE_LOST_RESPONSE.sector_list,
    //   successful: true,
    // };

    // Original implementation (commented out for now):
    try {
      const result = await servicePredictorApiService.calculateLost({
        ...params,
        ...(signal && { signal }),
      });

      if (result.canceled) {
        return rejectWithValue({ canceled: true });
      }

      if (result.error) {
        return rejectWithValue({ error: result.error });
      }

      return result;
    } catch (error: any) {
      return rejectWithValue({
        error: error.message || 'Failed to calculate lost sectors',
      });
    }
  }
);

const initialState: ServicePredictorState = {
  baseNodeList: null,
  baseNodeLoading: false,
  baseNodeError: null,

  operatorList: null,
  operatorNames: [],
  operatorLoading: false,
  operatorError: null,

  countryList: [],
  countryLoading: false,
  countryError: null,

  apiKeyValid: false,
  apiKeyOperator: null,
  apiKeyLoading: false,
  apiKeyError: null,

  sectorResults: null,
  sectorLoading: false,
  sectorError: null,

  selectedSite: null,

  remoteNode: {
    latitude: '',
    longitude: '',
    address: '',
    subscriber: '',
    location: null,
    findByAddress: true,
    addressError: false,
    addressErrorMsg: '',
    latlngUpdated: false,
    addressUpdated: false,
    aboveOpen: '',
    aboveRoof: '',
  },

  baseNode: {
    serial: '',
    cell: '',
    sector: '',
    hostname: '',
    latitude: '',
    longitude: '',
    azimuth: '',
    tilt: '',
    height_agl: '',
    height_gps: '',
    market_site: '',
    region: '',
    carrier: '',
    subscribers: '',
    planning_id: '',
    part_number: '',
    baseNodeOption: 'all',
    sectorOptions: {},
    disabled: true,
  },

  ui: {
    showInfoDiv: false,
    removeRemoteButton: false,
    showBNRadiusDiv: false,
    successful: false,
  },

  filters: {
    activeBands: ['3', '5', '6'],
    activeHeatmap: null,
    activeHeight: null,
  },

  calculatedRemoteNodeLocation: null,
};

const servicePredictorSlice = createSlice({
  name: 'servicePredictor',
  initialState,
  reducers: {
    clearBaseNodeError: (state) => {
      state.baseNodeError = null;
    },

    clearOperatorError: (state) => {
      state.operatorError = null;
    },

    clearCountryError: (state) => {
      state.countryError = null;
    },

    clearSectorError: (state) => {
      state.sectorError = null;
    },

    updateRemoteNodeCoordinates: (
      state,
      action: PayloadAction<{ lat: number; lng: number }>
    ) => {
      state.remoteNode.latitude = action.payload.lat.toFixed(6);
      state.remoteNode.longitude = action.payload.lng.toFixed(6);
      state.remoteNode.location = {
        lat: action.payload.lat,
        lng: action.payload.lng,
      };
      state.remoteNode.latlngUpdated = true;
      localStorage.setItem(
        'remote_lat_lng',
        JSON.stringify({
          lat: state.remoteNode.latitude,
          lng: state.remoteNode.longitude,
        })
      );
    },

    updateRemoteNodeAddress: (state, action: PayloadAction<string>) => {
      state.remoteNode.address = action.payload;
      state.remoteNode.addressUpdated = true;
      state.remoteNode.addressError = false;
      state.remoteNode.addressErrorMsg = '';
    },

    updateRemoteNodeSubscriber: (state, action: PayloadAction<string>) => {
      state.remoteNode.subscriber = action.payload;
    },

    updateRemoteNodeHeights: (
      state,
      action: PayloadAction<{ aboveOpen?: number; aboveRoof?: number }>
    ) => {
      if (action.payload.aboveOpen !== undefined) {
        state.remoteNode.aboveOpen = action.payload.aboveOpen.toString();
      }
      if (action.payload.aboveRoof !== undefined) {
        state.remoteNode.aboveRoof = action.payload.aboveRoof.toString();
      }
    },

    setRemoteNodeFindByAddress: (state, action: PayloadAction<boolean>) => {
      state.remoteNode.findByAddress = action.payload;
      state.remoteNode.addressError = false;
      state.remoteNode.addressErrorMsg = '';
    },

    setRemoteNodeAddressError: (state, action: PayloadAction<string>) => {
      state.remoteNode.addressError = true;
      state.remoteNode.addressErrorMsg = action.payload;
    },

    clearRemoteNode: (state) => {
      state.remoteNode = {
        latitude: '',
        longitude: '',
        address: '',
        subscriber: '',
        location: null,
        findByAddress: true,
        addressError: false,
        addressErrorMsg: '',
        latlngUpdated: false,
        addressUpdated: false,
        aboveOpen: '',
        aboveRoof: '',
      };
    },

    updateRemoteNodeFields: (
      state,
      action: PayloadAction<{
        address?: string;
        latitude?: number;
        longitude?: number;
        coordinates?: { lat: number; lng: number };
        subscriber?: string;
        heights?: { above_open?: number; above_roof?: number };
        clicked?: boolean;
        radius?: boolean;
      }>
    ) => {
      const data = action.payload;

      if (data.address) {
        state.remoteNode.address = data.address;
        state.remoteNode.location = null;
      }

      if (data.coordinates) {
        state.remoteNode.latitude = data.coordinates.lat.toFixed(6);
        state.remoteNode.longitude = data.coordinates.lng.toFixed(6);
        state.remoteNode.location = {
          lat: data.coordinates.lat,
          lng: data.coordinates.lng,
        };
      } else {
        if (data.latitude !== undefined) {
          state.remoteNode.latitude = data.latitude.toFixed(6);
        }
        if (data.longitude !== undefined) {
          state.remoteNode.longitude = data.longitude.toFixed(6);
        }
      }

      if (state.remoteNode.latitude && state.remoteNode.longitude) {
        localStorage.setItem(
          'remote_lat_lng',
          JSON.stringify({
            lat: state.remoteNode.latitude,
            lng: state.remoteNode.longitude,
          })
        );
      }

      if (data.heights) {
        if (data.heights.above_open !== undefined) {
          state.remoteNode.aboveOpen = data.heights.above_open.toString();
        }
        if (data.heights.above_roof !== undefined) {
          state.remoteNode.aboveRoof = data.heights.above_roof.toString();
        }
      }

      if (data.subscriber !== undefined) {
        state.remoteNode.subscriber = data.subscriber;
      }

      if (data.clicked) {
        state.remoteNode.addressUpdated = false;
        state.remoteNode.findByAddress = false;
      }
    },

    updateBaseNodeField: (
      state,
      action: PayloadAction<{ field: string; value: string }>
    ) => {
      const { field, value } = action.payload;
      if (field in state.baseNode) {
        (state.baseNode as any)[field] = value;
      }
    },

    updateBaseNodeOption: (
      state,
      action: PayloadAction<'all' | 'nearest' | 'use_specific'>
    ) => {
      state.baseNode.baseNodeOption = action.payload;
      state.baseNode.disabled = action.payload !== 'use_specific';
    },

    updateBaseNodeSectorOptions: (
      state,
      action: PayloadAction<Record<string, string>>
    ) => {
      state.baseNode.sectorOptions = action.payload;
    },

    updateBaseNodeFields: (
      state,
      action: PayloadAction<{
        serial?: string;
        cell?: string;
        sector?: string;
        hostname?: string;
        latitude?: number;
        longitude?: number;
        location?: string;
        azimuth?: number;
        downtilt?: number;
        height_agl?: number;
        height_gps?: number;
        market?: string;
        site?: string;
        region?: string;
        carrier?: any[];
        subscribers?: number;
        planning_id?: string;
        part_number?: string;
        sector_options?: Record<string, string>;
      }>
    ) => {
      const data = action.payload;

      if (data.serial !== undefined) state.baseNode.serial = data.serial;
      if (data.cell !== undefined) state.baseNode.cell = data.cell;
      if (data.sector !== undefined) state.baseNode.sector = data.sector;
      if (data.hostname !== undefined) state.baseNode.hostname = data.hostname;
      if (data.region !== undefined) state.baseNode.region = data.region;
      if (data.subscribers !== undefined)
        state.baseNode.subscribers = data.subscribers.toString();
      if (data.height_agl !== undefined)
        state.baseNode.height_agl = data.height_agl.toString();
      if (data.height_gps !== undefined)
        state.baseNode.height_gps = data.height_gps.toString();
      if (data.planning_id !== undefined)
        state.baseNode.planning_id = data.planning_id;
      if (data.part_number !== undefined)
        state.baseNode.part_number = data.part_number;
      if (data.latitude !== undefined)
        state.baseNode.latitude = data.latitude.toString();
      if (data.longitude !== undefined)
        state.baseNode.longitude = data.longitude.toString();
      if (data.azimuth !== undefined)
        state.baseNode.azimuth = data.azimuth.toString();
      if (data.downtilt !== undefined)
        state.baseNode.tilt = data.downtilt.toString();

      if (data.market !== undefined && data.site !== undefined) {
        state.baseNode.market_site = `${data.market} / ${data.site}`;
      }

      if (
        data.carrier &&
        Array.isArray(data.carrier) &&
        data.carrier.length > 0
      ) {
        const carrierValues = data.carrier
          .filter((item: any) => item.frequency !== 0)
          .map((item: any) => item.frequency)
          .join(', ');
        state.baseNode.carrier = carrierValues;
      }

      if (data.sector_options) {
        state.baseNode.sectorOptions = data.sector_options;
      }
    },

    clearBaseNode: (
      state,
      action: PayloadAction<{
        heatmap_exist?: boolean;
        results_exist?: boolean;
      }>
    ) => {
      const { heatmap_exist = false, results_exist = false } =
        action.payload || {};

      if (!heatmap_exist) {
        state.baseNode.serial = '';
        state.baseNode.cell = '';
        state.baseNode.sector = '';
        state.baseNode.hostname = '';
        state.baseNode.latitude = '';
        state.baseNode.longitude = '';
        state.baseNode.azimuth = '';
        state.baseNode.tilt = '';
        state.baseNode.height_agl = '';
        state.baseNode.height_gps = '';
        state.baseNode.market_site = '';
        state.baseNode.region = '';
        state.baseNode.carrier = '';
        state.baseNode.subscribers = '';
        state.baseNode.planning_id = '';
        state.baseNode.part_number = '';

        if (state.baseNode.baseNodeOption !== 'all') {
          state.baseNode.baseNodeOption = 'all';
          state.baseNode.disabled = true;
        }
      }

      if (!results_exist) {
        state.baseNode.sectorOptions = {};
      }
    },

    // UI State Actions
    setShowInfoDiv: (state, action: PayloadAction<boolean>) => {
      state.ui.showInfoDiv = action.payload;
    },

    toggleShowInfoDiv: (state) => {
      state.ui.showInfoDiv = !state.ui.showInfoDiv;
    },

    setRemoveRemoteButton: (state, action: PayloadAction<boolean>) => {
      state.ui.removeRemoteButton = action.payload;
    },

    setShowBNRadiusDiv: (state, action: PayloadAction<boolean>) => {
      state.ui.showBNRadiusDiv = action.payload;
    },

    toggleShowBNRadiusDiv: (state) => {
      state.ui.showBNRadiusDiv = !state.ui.showBNRadiusDiv;
    },

    setSuccessful: (state, action: PayloadAction<boolean>) => {
      state.ui.successful = action.payload;
    },

    setSelectedSite: (state, action: PayloadAction<SiteInfo | null>) => {
      state.selectedSite = action.payload;
    },

    clearSelectedSite: (state) => {
      state.selectedSite = null;
    },

    // Filter Actions
    setActiveBands: (state, action: PayloadAction<string[]>) => {
      state.filters.activeBands = action.payload;
    },

    setActiveHeatmap: (state, action: PayloadAction<string | null>) => {
      state.filters.activeHeatmap = action.payload;
    },

    setActiveHeight: (state, action: PayloadAction<string | null>) => {
      state.filters.activeHeight = action.payload;
    },

    clearSectorResults: (state) => {
      state.sectorResults = null;
      state.ui.successful = false;
      state.ui.showInfoDiv = false;
      state.filters.activeHeatmap = null;
      state.filters.activeHeight = null;
      state.calculatedRemoteNodeLocation = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(findBNs.pending, (state) => {
        state.baseNodeLoading = true;
        state.baseNodeError = null;
      })
      .addCase(
        findBNs.fulfilled,
        (state, action: PayloadAction<FindBNsResponse>) => {
          state.baseNodeLoading = false;
          state.baseNodeList = action.payload.baseNodeList?.bn_list || null;
          if (action.payload.baseNodeList?.bn_list?.length > 0) {
            state.ui.showBNRadiusDiv = true;
          }
        }
      )
      .addCase(findBNs.rejected, (state, action) => {
        state.baseNodeLoading = false;
        if (!action.payload?.canceled) {
          state.baseNodeError =
            action.payload?.error || 'Failed to find base nodes';
        }
      });

    builder
      .addCase(findOperators.pending, (state) => {
        state.operatorLoading = true;
        state.operatorError = null;
      })
      .addCase(
        findOperators.fulfilled,
        (state, action: PayloadAction<FindOperatorsResponse>) => {
          state.operatorLoading = false;
          state.operatorList = action.payload.operatorList || null;
          state.operatorNames = action.payload.operatorNames || [];
        }
      )
      .addCase(findOperators.rejected, (state, action) => {
        state.operatorLoading = false;
        if (!action.payload?.canceled) {
          state.operatorError =
            action.payload?.error || 'Failed to find operators';
        }
      });

    builder
      .addCase(loadCountryList.pending, (state) => {
        state.countryLoading = true;
        state.countryError = null;
      })
      .addCase(loadCountryList.fulfilled, (state, action) => {
        state.countryLoading = false;
        state.countryList = action.payload.countryList || [];
      })
      .addCase(loadCountryList.rejected, (state, action) => {
        state.countryLoading = false;
        if (!action.payload?.canceled) {
          state.countryError =
            action.payload?.error || 'Failed to load country list';
        }
      });

    builder
      .addCase(calculateLost.pending, (state) => {
        state.sectorLoading = true;
        state.sectorError = null;
        state.ui.successful = false;
        state.ui.showInfoDiv = false;
        state.sectorResults = null;
        state.calculatedRemoteNodeLocation = null;
      })
      .addCase(calculateLost.fulfilled, (state, action) => {
        state.sectorLoading = false;
        state.sectorResults = action.payload.sectorResults || null;

        const remoteNodeData = (action.meta.arg as CalculateLostRequest)?.params
          ?.remoteNodeData;
        if (remoteNodeData?.address) {
          state.calculatedRemoteNodeLocation = {
            address: remoteNodeData.address,
          };
        } else if (
          remoteNodeData?.latitude !== undefined &&
          remoteNodeData?.longitude !== undefined
        ) {
          state.calculatedRemoteNodeLocation = {
            latitude: remoteNodeData.latitude,
            longitude: remoteNodeData.longitude,
          };
        } else {
          state.calculatedRemoteNodeLocation = null;
        }

        if (action.payload.successful && action.payload.sectorResults) {
          state.ui.successful = true;
          state.ui.showInfoDiv = true;
          state.ui.removeRemoteButton = false;
        }
      })
      .addCase(calculateLost.rejected, (state, action) => {
        state.sectorLoading = false;
        if (!action.payload?.canceled) {
          state.sectorError =
            action.payload?.error || 'Failed to calculate lost sectors';
        }
      });
  },
});

export const {
  clearBaseNodeError,
  clearOperatorError,
  clearCountryError,
  clearSectorError,
  updateRemoteNodeCoordinates,
  updateRemoteNodeAddress,
  updateRemoteNodeSubscriber,
  updateRemoteNodeHeights,
  setRemoteNodeFindByAddress,
  setRemoteNodeAddressError,
  clearRemoteNode,
  updateRemoteNodeFields,
  updateBaseNodeField,
  updateBaseNodeOption,
  updateBaseNodeSectorOptions,
  updateBaseNodeFields,
  clearBaseNode,
  setShowInfoDiv,
  toggleShowInfoDiv,
  setRemoveRemoteButton,
  setShowBNRadiusDiv,
  toggleShowBNRadiusDiv,
  setSuccessful,
  setSelectedSite,
  clearSelectedSite,
  setActiveBands,
  setActiveHeatmap,
  setActiveHeight,
  clearSectorResults,
} = servicePredictorSlice.actions;

export default servicePredictorSlice.reducer;
